import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DeleteOutlineOutlinedIcon,
  KeyboardBackspaceIcon,
  LockOutlinedIcon,
  KeyOutlinedIcon,
  AddRoundedIcon,
  BadgeOutlinedIcon,
} from "../../utilities/icons";
import axios from "axios";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import {
  deleteAllAccess,
  deleteUserAccess,
  editAccess,
  projectsApi,
} from "../../apiCalls/Apicalls";
import { extractProjectAccessLabels } from "../../utilities/projectAccess";

const BLUE_OVERLAY = "rgba(65, 139, 255, 0.12)";
const BLUE = "#418BFF";

function getInitials(selectedRow) {
  return `${selectedRow?.FirstName || ""} ${selectedRow?.LastName || ""}`
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function getRoleBadge(selectedRow) {
  const department = selectedRow?.SpaceName || "";

  if (department.toLowerCase().includes("full")) {
    return { label: "Developer" };
  }
  if (department.toLowerCase().includes("qa")) {
    return { label: "QA Engineer" };
  }
  if (department.toLowerCase().includes("ui")) {
    return { label: "Designer" };
  }

  return { label: "Analyst" };
}

function getModuleMeta(area) {
  const prefix = area?.split("-")?.[1] || "ACCESS";
  return { moduleName: `${prefix} module` };
}

function UserAccess({
  open,
  onClose,
  selectedRow,
  areas,
  setAreas,
  selectedAreas,
  setSelectedAreas,
  filteredOptions,
  setFilteredOptions,
  fetchEndUsersWithAccess,
}) {
  const [accessData, setAccessData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [savingAccess, setSavingAccess] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deletingArea, setDeletingArea] = useState("");

  const fullName =
    `${selectedRow?.FirstName || ""} ${selectedRow?.LastName || ""}`.trim() ||
    selectedRow?.FullName ||
    "Selected user";
  const roleBadge = getRoleBadge(selectedRow);
  const empid = selectedRow?._id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(projectsApi);
        setAccessData(extractProjectAccessLabels(response.data || []));
      } catch (error) {
        console.error("Error fetching access labels:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const nextFilteredOptions = accessData.filter(
      (option) => !areas?.includes(option),
    );
    setFilteredOptions(nextFilteredOptions);
  }, [accessData, areas, setFilteredOptions]);

  useEffect(() => {
    if (selectedRow?.Access) {
      setAreas(selectedRow.Access);
    } else {
      setAreas([]);
    }
    setSelectedAreas([]);
    setSearchValue("");
  }, [selectedRow, setAreas, setSelectedAreas]);

  const matchingAreas = useMemo(() => {
    if (!searchValue.trim()) {
      return [];
    }

    const query = searchValue.toLowerCase();
    return (filteredOptions || []).filter((option) =>
      option.toLowerCase().includes(query),
    );
  }, [filteredOptions, searchValue]);

  const panelBadges = useMemo(() => {
    const list = [
      roleBadge,
      {
        label: `${areas?.length || 0} area${areas?.length === 1 ? "" : "s"} assigned`,
      },
    ];
    if (selectedAreas?.length) {
      list.push({ label: `${selectedAreas.length} pending`, pending: true });
    }
    return list;
  }, [areas?.length, roleBadge, selectedAreas?.length]);

  const handleAddArea = () => {
    const value = searchValue.trim().toUpperCase();

    if (!value) {
      return;
    }

    if (areas?.includes(value) || selectedAreas?.includes(value)) {
      setSearchValue("");
      return;
    }

    setSelectedAreas((previous) => [...previous, value]);
    setSearchValue("");
  };

  const handleSelectSuggestion = (area) => {
    if (selectedAreas?.includes(area)) {
      setSearchValue("");
      return;
    }

    setSelectedAreas((previous) => [...previous, area]);
    setSearchValue("");
  };

  const handleRemovePendingArea = (area) => {
    setSelectedAreas((previous) => previous.filter((item) => item !== area));
  };

  const handleSaveAccess = async () => {
    if (!selectedAreas?.length) {
      toast.error("Please select at least one area.");
      return;
    }

    if (!empid || savingAccess) {
      return;
    }

    try {
      setSavingAccess(true);
      const config = {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "application/json",
        },
      };

      await axios.put(
        `${editAccess}/${empid}`,
        { Access: selectedAreas },
        config,
      );

      toast.success("Access updated successfully");
      await fetchEndUsersWithAccess();
      onClose();
      setSelectedAreas([]);
      setSearchValue("");
    } catch (error) {
      toast.error("Error updating access areas");
    } finally {
      setSavingAccess(false);
    }
  };

  const handleDeleteArea = async (area) => {
    if (!selectedRow?._id || deletingArea) {
      return;
    }

    try {
      setDeletingArea(area);
      const previousAreas = areas;
      setAreas((prevAreas) => prevAreas?.filter((item) => item !== area));

      const config = {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "application/json",
        },
      };

      const response = await axios.delete(
        `${deleteUserAccess}/${selectedRow._id}/${area}`,
        config,
      );

      if (response.status === 200) {
        toast.success("Access deleted successfully");
        await fetchEndUsersWithAccess();
      } else {
        setAreas(previousAreas);
      }
    } catch (error) {
      console.error("Error deleting area:", error);
      toast.error("Failed to delete access");
      if (selectedRow?.Access) {
        setAreas(selectedRow.Access);
      }
    } finally {
      setDeletingArea("");
    }
  };

  const handleDeleteAllAccess = async () => {
    if (!empid || deletingAll || !areas?.length) {
      return;
    }

    try {
      setDeletingAll(true);
      const config = {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "application/json",
        },
        data: {
          Access: [],
          SpaceName: "",
        },
      };

      const response = await axios.delete(
        `${deleteAllAccess}/${empid}`,
        config,
      );

      if (response.status === 200) {
        toast.success("All access deleted successfully");
        setAreas([]);
        setSelectedAreas([]);
        await fetchEndUsersWithAccess();
        onClose();
      }
    } catch (error) {
      console.error("Error deleting access:", error);
      toast.error("Failed to delete access");
    } finally {
      setDeletingAll(false);
    }
  };

  const isBusy = savingAccess || deletingAll || Boolean(deletingArea);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={isBusy ? undefined : onClose}
      className="ax-drawer"
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          background: "#fff",
          boxShadow: "var(--ax-shadow-lg)",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          borderBottom="1px solid var(--ax-border)"
        >
          <Typography
            sx={{
              fontFamily: "var(--ax-font-heading)",
              fontSize: "0.98rem",
              fontWeight: 500,
              letterSpacing: "-0.015em",
              color: "var(--ax-ink)",
            }}
          >
            User access settings
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={handleDeleteAllAccess}
              disabled={!areas?.length || isBusy}
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
              size="small"
              sx={{ fontSize: "0.78rem" }}
            >
              {deletingAll ? "Deleting..." : "Delete all"}
            </Button>
            <IconButton onClick={onClose} disabled={isBusy} size="small">
              <KeyboardBackspaceIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Box
          sx={{
            px: 3,
            py: 2.5,
            background: "var(--ax-bg-secondary)",
            borderBottom: "1px solid var(--ax-border)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: BLUE_OVERLAY,
                color: BLUE,
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "12px",
              }}
            >
              {getInitials(selectedRow)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.96rem",
                  fontWeight: 600,
                  color: "var(--ax-ink)",
                }}
                noWrap
              >
                {fullName}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.25}>
                <Typography
                  sx={{ fontSize: "0.74rem", color: "var(--ax-ink-2)" }}
                >
                  {selectedRow?.EmpId || "N/A"}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.74rem", color: "var(--ax-ink-3)" }}
                >
                  ·
                </Typography>
                <Typography
                  sx={{ fontSize: "0.74rem", color: "var(--ax-ink-2)" }}
                >
                  {selectedRow?.SpaceName || "No department"}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5}>
            {panelBadges.map((badge) => (
              <Chip
                key={badge.label}
                icon={
                  <BadgeOutlinedIcon
                    sx={{ fontSize: "13px !important" }}
                  />
                }
                label={badge.label}
                size="small"
                sx={{
                  bgcolor: badge.pending
                    ? "var(--ax-amber-soft)"
                    : "var(--ax-surface)",
                  color: badge.pending
                    ? "var(--ax-amber)"
                    : "var(--ax-ink-2)",
                  fontWeight: 500,
                  fontSize: "0.72rem",
                  border: badge.pending
                    ? "none"
                    : "1px solid var(--ax-border)",
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid var(--ax-border)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flex: 1, position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search or add an area..."
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                disabled={isBusy}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon
                        fontSize="small"
                        sx={{ color: "var(--ax-ink-3)" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {matchingAreas.length > 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    overflow: "hidden",
                    border: "1px solid var(--ax-border)",
                    borderRadius: "10px",
                    boxShadow: "var(--ax-shadow)",
                  }}
                >
                  <List dense disablePadding>
                    {matchingAreas.slice(0, 6).map((area) => (
                      <ListItemButton
                        key={area}
                        onClick={() => handleSelectSuggestion(area)}
                      >
                        <ListItemText
                          primary={area}
                          primaryTypographyProps={{
                            fontSize: "0.82rem",
                            fontWeight: 500,
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              ) : null}
            </Box>
            <Button
              variant="contained"
              onClick={handleAddArea}
              disabled={isBusy || !searchValue.trim()}
              startIcon={<AddRoundedIcon fontSize="small" />}
              size="small"
              sx={{
                whiteSpace: "nowrap",
                px: 2,
                py: "8px",
              }}
            >
              Add
            </Button>
          </Stack>

          {selectedAreas?.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5}>
              {selectedAreas.map((area) => (
                <Chip
                  key={area}
                  label={area}
                  size="small"
                  onDelete={
                    isBusy ? undefined : () => handleRemovePendingArea(area)
                  }
                  sx={{
                    borderRadius: "6px",
                    bgcolor: "var(--ax-amber-soft)",
                    color: "var(--ax-amber)",
                    fontSize: "0.74rem",
                    fontWeight: 500,
                  }}
                />
              ))}
            </Stack>
          ) : null}
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={1.5}
        >
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: "var(--ax-ink-2)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontWeight: 600,
            }}
          >
            Areas
          </Typography>
          <Typography
            sx={{ fontSize: "0.74rem", color: "var(--ax-ink-2)" }}
          >
            {areas?.length || 0} total
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: "var(--ax-border)" }} />

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {areas?.length > 0 ? (
            areas.map((area) => {
              const moduleMeta = getModuleMeta(area);
              const isDeletingThisArea = deletingArea === area;

              return (
                <Stack
                  key={area}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  px={3}
                  py={1.5}
                  borderBottom="1px solid var(--ax-border)"
                  sx={{
                    "&:hover": { bgcolor: "var(--ax-surface-hover)" },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: BLUE_OVERLAY,
                        color: BLUE,
                      }}
                    >
                      <KeyOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.86rem",
                          fontWeight: 600,
                          color: "var(--ax-ink)",
                        }}
                      >
                        {area}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "var(--ax-ink-3)",
                        }}
                      >
                        {moduleMeta.moduleName}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    onClick={() => handleDeleteArea(area)}
                    disabled={isBusy}
                    size="small"
                    sx={{
                      minWidth: 0,
                      p: "6px 8px",
                      color: isDeletingThisArea
                        ? "var(--ax-amber)"
                        : "var(--ax-ink-3)",
                      "&:hover": {
                        bgcolor: "var(--ax-red-soft)",
                        color: "var(--ax-red)",
                      },
                    }}
                  >
                    {isDeletingThisArea ? (
                      "Removing..."
                    ) : (
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    )}
                  </Button>
                </Stack>
              );
            })
          ) : (
            <Stack
              spacing={1.5}
              alignItems="center"
              justifyContent="center"
              sx={{ height: "100%", px: 3, textAlign: "center", py: 6 }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  background: "var(--ax-bg-secondary)",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--ax-ink-3)",
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography
                sx={{ color: "var(--ax-ink-2)", fontSize: "0.86rem" }}
              >
                No areas assigned yet
              </Typography>
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.25}
          px={3}
          py={2}
          borderTop="1px solid var(--ax-border)"
        >
          <Button variant="outlined" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAccess}
            disabled={!selectedAreas?.length || isBusy}
          >
            {savingAccess ? "Saving..." : "Save changes"}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}

export default UserAccess;
